import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import StaffPageShell from '../components/StaffPageShell';
import LabHostsPanel from '../components/LabHostsPanel';
import { ThemeButton } from '../components/ThemeWrapper';
import usePermissions from '../hooks/usePermissions';
import useLabCycle from '../hooks/useLabCycle';

const STATS = [
  ['smsWithUnix', 'In SMS with a Linux name'],
  ['loggedInNow', 'Logged in now'],
  ['onServerNotInSms', 'On a server, not in SMS'],
  ['expiringSoon', 'Expiring within 15 days'],
  ['disableDue', 'Disable due'],
  ['deleteDue', 'Delete due'],
  ['placedStillOpen', 'Placed, login still open'],
  ['critical', 'Critical warnings'],
];

function severityStyle(severity) {
  if (severity === 'critical') return { color: '#b91c1c', fontWeight: 600 };
  if (severity === 'warning') return { color: '#a16207', fontWeight: 600 };
  return undefined;
}

function LabCyclePage() {
  const { hasPermission, role } = usePermissions();
  const { watch, loading, error, notice, actionKey, checkingServers, load, act, checkServers } = useLabCycle({
    extendExpiry: role === 'superadmin',
  });
  const [query, setQuery] = useState('');
  const [warningsOnly, setWarningsOnly] = useState(false);

  const accounts = useMemo(() => {
    const rows = watch?.accounts || [];
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (warningsOnly && row.severity === 'ok') return false;
      if (!q) return true;
      return [row.fullName, row.studentId, row.unixUsername, row.email, row.courseName, row.batchName]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [watch, query, warningsOnly]);

  if (!hasPermission('import.pma')) {
    return (
      <StaffPageShell title="Lab user cycle" subtitle="Lab permission required">
        <p className="text-sm theme-text-secondary">This page needs the Lab Linux users permission.</p>
      </StaffPageShell>
    );
  }

  const summary = watch?.summary || {};

  return (
    <StaffPageShell
      wide
      title="Lab user cycle"
      subtitle="SMS students with a Linux name, who is logged in on the lab servers, and the disable / enable / delete action the policy calls for."
    >
      <div className="profile-card theme-animate p-6 space-y-5">
        <p className="text-sm theme-text-secondary">
          PO regular expires after 9 months from the batch start, then stays in a 45-day disable window before delete.
          A placed student should be disabled. This list refreshes from SMS every 20 seconds.{' '}
          <Link className="underline" to="/admin/pma-student-import">
            Lab Linux users
          </Link>{' '}
          is where a missing account is created. Login lock, VNC, storage, and the keep or remove view are on{' '}
          <Link className="underline" to="/admin/lab-fleet">
            Server accounts
          </Link>
          .
        </p>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {STATS.map(([key, label]) => (
            <div key={key} className="rounded-lg p-3" style={{ border: '1px solid var(--color-border)' }}>
              <div className="text-xs theme-text-secondary">{label}</div>
              <div className="text-2xl font-semibold theme-text">{summary[key] ?? (loading ? '…' : 0)}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <ThemeButton type="button" onClick={() => load()} disabled={loading || checkingServers}>
            {loading ? 'Loading…' : 'Refresh SMS'}
          </ThemeButton>
          <ThemeButton type="button" onClick={checkServers} disabled={checkingServers || Boolean(actionKey)}>
            {checkingServers ? 'Checking servers…' : 'Check servers now'}
          </ThemeButton>
          <label className="text-sm theme-text-secondary flex items-center gap-2">
            <input
              type="checkbox"
              checked={warningsOnly}
              onChange={(event) => setWarningsOnly(event.target.checked)}
            />
            Warnings only
          </label>
          <input
            className="px-3 py-2 border rounded text-sm"
            placeholder="Search name, VE ID, or Linux name"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        <p className="text-xs theme-text-secondary">
          {watch?.inventoriedAt
            ? `Servers last checked ${new Date(watch.inventoriedAt).toLocaleString()}.`
            : 'No server inventory yet. Check servers now.'}{' '}
          {watch?.sessionLimitNote}
        </p>

        {notice ? <p className="text-sm theme-text">{notice}</p> : null}
        {error ? (
          <p className="text-sm text-red-700" role="alert">
            {error}
          </p>
        ) : null}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm theme-text">
            <thead>
              <tr className="text-left theme-text-secondary">
                <th className="pr-3 py-2">Student</th>
                <th className="pr-3 py-2">Linux name</th>
                <th className="pr-3 py-2">Course</th>
                <th className="pr-3 py-2">Logged in</th>
                <th className="pr-3 py-2">Lab status</th>
                <th className="pr-3 py-2">Expiry</th>
                <th className="pr-3 py-2">Delete</th>
                <th className="pr-3 py-2">Policy</th>
                <th className="pr-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((row) => {
                const busy = actionKey.startsWith(`${row.unixUsername}:`);
                return (
                  <tr key={row.studentId} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td className="pr-3 py-2 align-top">
                      <div>{row.fullName}</div>
                      <div className="text-xs theme-text-secondary">{row.studentId}</div>
                    </td>
                    <td className="pr-3 py-2 align-top font-mono text-xs">{row.unixUsername}</td>
                    <td className="pr-3 py-2 align-top">
                      <div>{row.courseName || '—'}</div>
                      <div className="text-xs theme-text-secondary">{row.batchName}</div>
                    </td>
                    <td className="pr-3 py-2 align-top">
                      {row.loggedIn ? row.sessionHosts.join(', ') : '—'}
                    </td>
                    <td className="pr-3 py-2 align-top">
                      <div>{row.serverStatusLabel || row.labStatus || 'No lab row'}</div>
                      {row.resourceLabel ? <div className="text-xs theme-text-secondary">{row.resourceLabel}</div> : null}
                    </td>
                    <td className="pr-3 py-2 align-top">{row.expiryDate || '—'}</td>
                    <td className="pr-3 py-2 align-top">{row.deleteDate || '—'}</td>
                    <td className="pr-3 py-2 align-top" style={severityStyle(row.severity)}>
                      {row.warnings.length
                        ? row.warnings.map((item) => item.message).join(' ')
                        : row.exempt
                          ? 'No expiry'
                          : 'Within policy'}
                    </td>
                    <td className="pr-3 py-2 align-top">
                      <div className="flex flex-wrap gap-2">
                        <ThemeButton
                          type="button"
                          onClick={() => act(row, 'disable')}
                          disabled={!row.canAct || busy || checkingServers}
                        >
                          Disable
                        </ThemeButton>
                        <ThemeButton
                          type="button"
                          onClick={() => act(row, 'enable')}
                          disabled={!row.canAct || busy || checkingServers}
                        >
                          Enable
                        </ThemeButton>
                        <ThemeButton
                          type="button"
                          onClick={() => act(row, 'delete')}
                          disabled={!row.canAct || busy || checkingServers}
                        >
                          Delete
                        </ThemeButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!loading && !accounts.length ? (
            <p className="text-sm theme-text-secondary py-3">No matching students with a Linux username.</p>
          ) : null}
        </div>

        <LabHostsPanel />

        {(watch?.unknownSessions || []).length ? (
          <div className="space-y-2">
            <h2 className="text-base font-semibold theme-text">Logged in on a server, not in SMS</h2>
            <ul className="text-sm space-y-1">
              {watch.unknownSessions.map((item) => (
                <li key={item.unixUsername} style={severityStyle('warning')}>
                  <span className="font-mono">{item.unixUsername}</span> on {item.hosts.join(', ')}. {item.warnings[0]?.message}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </StaffPageShell>
  );
}

export default LabCyclePage;
